// src/pages/AdminUserProfile.js
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { query, where, collection, getDocs, doc, getDoc, orderBy, updateDoc } from "firebase/firestore";
import { db } from "../firebase.js";
import { Container, Card, Table, Button, Row, Col, Spinner, Tabs, Tab, Badge, Alert, ProgressBar, Form } from "react-bootstrap";
import { format, parseISO } from "date-fns";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";
import { FileText, Award, CheckCircle, Download, Pencil, RefreshCw } from "lucide-react";
import { useLoadScript } from "@react-google-maps/api";
import { getTravelMinutes } from "../utils/maps.js";
import { roundTravelMinute, floorTo5 } from "../utils/travelCalculations.js";
import DateRangeFilter from "../components/common/DateRangeFilter.js";
import { useReportFilters } from "../hooks/useReportFilters.js";
import { computeReportMetrics } from "../utils/reportMetrics.js";
import ReportMobileCard from "../components/common/ReportMobileCard.js";

const LIBRARIES = ["places"];

export default function AdminUserProfile() {
  const { uid: targetUid } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [reports, setReports] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("reports");
  const [recalculating, setRecalculating] = useState({});
  const filters = useReportFilters(reports);

  // Google Maps
  const loaderOpts = useMemo(
    () => ({
      id: "google-map-script",
      googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY,
      libraries: LIBRARIES
    }),
    []
  );
  const { isLoaded } = useLoadScript(loaderOpts);

  useEffect(() => {
    if (!user?.isAdmin) return;
    loadAllData();
  }, [user, targetUid]);

  const loadAllData = async () => {
    try {
      setLoading(true);

      // Load user data
      const userDoc = await getDoc(doc(db, "users", targetUid));
      if (userDoc.exists()) {
        setUserData({ uid: userDoc.id, ...userDoc.data() });
      }

      // Load weekly reports
      const reportsQuery = query(
        collection(db, "weeklyReports"),
        where("uid", "==", targetUid)
      );
      const reportsSnap = await getDocs(reportsQuery);
      setReports(reportsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load document signatures
      const signaturesQuery = query(
        collection(db, "documentSignatures"),
        where("userId", "==", targetUid),
        orderBy("signedAt", "desc")
      );
      const signaturesSnap = await getDocs(signaturesQuery);
      setSignatures(signaturesSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load quiz results
      const quizQuery = query(
        collection(db, "quizResults"),
        where("userId", "==", targetUid),
        orderBy("completedAt", "desc")
      );
      const quizSnap = await getDocs(quizQuery);
      setQuizResults(quizSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    } catch (error) {
      console.error("Error loading user data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Salary config - lê do documento do usuário (editável em /admin/users)
  const salaryConfig = useMemo(() => {
    if (!userData?.hourlyRate) return null;
    return {
      rate: parseFloat(userData.hourlyRate) || 0,
      deduction: parseFloat(userData.deductionRate) || 0
    };
  }, [userData]);

  const exportToCSV = () => {
    let csv = "Type,Date,Details,Status\n";

    // Add signatures
    signatures.forEach(sig => {
      const date = sig.signedAt?.toDate?.().toLocaleString() || "N/A";
      csv += `Signature,"${date}","${sig.documentTitle} v${sig.documentVersion}",Signed\n`;
    });

    // Add quiz results
    quizResults.forEach(result => {
      const date = result.completedAt?.toDate?.().toLocaleString() || "N/A";
      csv += `Quiz,"${date}","${result.quizTitle} - Score: ${result.score}/${result.totalQuestions}",${result.passed ? "Passed" : "Failed"}\n`;
    });

    // Download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${userData?.displayName || "user"}_health_safety.csv`;
    a.click();
  };

  const exportToJSON = () => {
    const data = {
      user: userData,
      signatures: signatures.map(sig => ({
        ...sig,
        signedAt: sig.signedAt?.toDate?.().toISOString()
      })),
      quizResults: quizResults.map(result => ({
        ...result,
        completedAt: result.completedAt?.toDate?.().toISOString(),
        startedAt: result.startedAt?.toDate?.().toISOString()
      }))
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${userData?.displayName || "user"}_health_safety.json`;
    a.click();
  };

  // Função para recalcular trajetos de um relatório
  const recalculateTravel = async (report) => {
    if (!isLoaded || !window.google?.maps) {
      alert("Google Maps ainda não carregou. Aguarde alguns segundos e tente novamente.");
      return;
    }

    setRecalculating(prev => ({ ...prev, [report.id]: true }));

    try {
      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
      const newTravelMinDay = {};

      // Precisa carregar a lista de clientes para pegar os endereços
      const clientsSnap = await getDocs(collection(db, "clients"));
      const clients = clientsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Para cada dia, recalcula os trajetos
      for (const day of days) {
        const entries = report.days?.[day] || [];
        if (entries.length < 2) {
          newTravelMinDay[day] = 0;
          continue;
        }

        // Calcula trajetos (mesma lógica de ColaboradorPage.js)
        const travelData = [];
        for (let i = 0; i < entries.length - 1; i++) {
          const o = entries[i].__manual
            ? entries[i].__address
            : clients.find(c => c.name === entries[i].client)?.address;
          const d = entries[i + 1].__manual
            ? entries[i + 1].__address
            : clients.find(c => c.name === entries[i + 1].client)?.address;

          if (o && d) {
            const travelTime = await getTravelMinutes(o, d);
            travelData.push({ index: i, travelTime });
          }
        }

        // roundTravelMinute e floorTo5 importados de travelCalculations.js

        let travelWithoutFirst = 0;
        for (let j = 0; j < travelData.length; j++) {
          const { index, travelTime } = travelData[j];
          if (index === 0) continue;
          const rounded = roundTravelMinute(travelTime);
          travelWithoutFirst += rounded;
        }

        let travel = 0;
        for (let j = 0; j < travelData.length; j++) {
          const { index, travelTime } = travelData[j];
          if (index === 0) {
            if (travelTime < 5 && travelTime > 0) {
              const wouldBeTotal = travelTime + travelWithoutFirst;
              if (wouldBeTotal < 5) {
                travel += travelTime;
              }
            } else if (travelTime >= 5) {
              const rounded = roundTravelMinute(travelTime);
              travel += rounded;
            }
          } else {
            const rounded = roundTravelMinute(travelTime);
            travel += rounded;
          }
        }

        if (travel > 5) {
          travel = floorTo5(travel);
        }

        newTravelMinDay[day] = travel;
      }

      // Atualiza no Firestore
      const docRef = doc(db, "weeklyReports", report.id);
      await updateDoc(docRef, { travelMinDay: newTravelMinDay });

      // Atualiza estado local
      setReports(prev => prev.map(r =>
        r.id === report.id ? { ...r, travelMinDay: newTravelMinDay } : r
      ));

      alert("Trajetos recalculados com sucesso!");
    } catch (error) {
      console.error("Erro ao recalcular trajetos:", error);
      alert("Erro ao recalcular trajetos: " + error.message);
    } finally {
      setRecalculating(prev => ({ ...prev, [report.id]: false }));
    }
  };

  if (!user?.isAdmin) return null;

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Carregando dados do usuário...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Button variant="link" onClick={() => navigate("/admin/users")}>
        ← Voltar aos Usuários
      </Button>

      {/* User Header */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col>
              <h3 className="mb-2">{userData?.displayName || userData?.email}</h3>
              <p className="text-muted mb-0">
                <strong>Email:</strong> {userData?.email} |
                <strong> Admin:</strong> {userData?.isAdmin ? " Sim" : " Não"}
              </p>
            </Col>
            <Col xs="auto">
              <Button variant="outline-primary" size="sm" className="me-2" onClick={exportToCSV}>
                <Download size={16} className="me-1" />
                Exportar CSV
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={exportToJSON}>
                <Download size={16} className="me-1" />
                Exportar JSON
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center shadow-sm">
            <Card.Body>
              <FileText size={32} className="text-primary mb-2" />
              <h4>{signatures.length}</h4>
              <p className="text-muted mb-0">Documentos Assinados</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center shadow-sm">
            <Card.Body>
              <Award size={32} className="text-warning mb-2" />
              <h4>{quizResults.length}</h4>
              <p className="text-muted mb-0">Tentativas de Quiz</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center shadow-sm">
            <Card.Body>
              <CheckCircle size={32} className="text-success mb-2" />
              <h4>{quizResults.filter(q => q.passed).length}</h4>
              <p className="text-muted mb-0">Quiz Aprovado</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        {/* Weekly Reports Tab */}
        <Tab eventKey="reports" title={t(language, "myWeeklyReports")}>
          {reports.length === 0 ? (
            <Alert variant="info">{t(language, "noReportsFor")} "{userData?.displayName}".</Alert>
          ) : (
            <>
              <DateRangeFilter
                startDate={filters.startDate}
                endDate={filters.endDate}
                onStartDateChange={filters.setStartDate}
                onEndDateChange={filters.setEndDate}
                onClear={filters.clearFilters}
                sortOrder={filters.sortOrder}
                onSortChange={filters.setSortOrder}
              />

              {filters.filteredAndSorted.length === 0 && (filters.startDate || filters.endDate) && (
                <Alert variant="warning" className="d-flex align-items-center gap-2">
                  <span>ℹ️</span>
                  <div>
                    <strong>{t(language, "noResultsForFilter")}</strong>
                    <p className="mb-0 text-muted small">{t(language, "tryDifferentDates")}</p>
                  </div>
                </Alert>
              )}

              {filters.filteredAndSorted.length > 0 && (<>
              {/* Desktop Table */}
              <Card className="shadow-sm border-0 overflow-hidden d-none d-md-block">
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle" style={{ fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f4f8', borderBottom: '2px solid #dee2e6' }}>
                        <th className="py-3 ps-3">Semana</th>
                        <th className="py-3 text-center">Trabalhado</th>
                        <th className="py-3 text-center">Lunch</th>
                        <th className="py-3 text-center">Trajeto</th>
                        <th className="py-3 text-center">Fechamento</th>
                        {salaryConfig && <th className="py-3 text-center">Salário</th>}
                        <th className="py-3">Última Edição</th>
                        <th className="py-3 text-center" style={{ minWidth: '110px' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filters.filteredAndSorted.map(r => {
                        const dt = parseISO(r.weekStart);
                        const weekLabel = format(dt, "dd MMM yyyy");
                        const totalWorked = r.weekWorkHours || r.weekTotalHours || "00:00";
                        const sumLunch = Object.values(r.lunchMinDay || {}).reduce((a, b) => a + (b || 0), 0);
                        const totalLunch = `${String(Math.floor(sumLunch / 60)).padStart(2, "0")}:${String(sumLunch % 60).padStart(2, "0")}`;
                        const sumTravel = Object.values(r.travelMinDay || {}).reduce((a, b) => a + (b || 0), 0);
                        const totalTravel = `${String(Math.floor(sumTravel / 60)).padStart(2, "0")}:${String(sumTravel % 60).padStart(2, "0")}`;
                        const [hW, mW] = totalWorked.split(":").map(n => parseInt(n) || 0);
                        const closingMin = hW * 60 + mW + sumLunch + sumTravel;
                        const fechamentoTotal = `${String(Math.floor(closingMin / 60)).padStart(2, "0")}:${String(closingMin % 60).padStart(2, "0")}`;
                        const lastHist = r.history?.slice(-1)[0];
                        const slug = encodeURIComponent(userData?.displayName || userData?.email);

                        let salaryText = null;
                        let salaryGross = null;
                        if (salaryConfig) {
                          const totalHrs = closingMin / 60;
                          const gross = totalHrs * salaryConfig.rate;
                          if (salaryConfig.deduction > 0) {
                            salaryText = (gross - gross * salaryConfig.deduction).toFixed(2);
                            salaryGross = gross.toFixed(2);
                          } else {
                            salaryText = gross.toFixed(2);
                          }
                        }

                        return (
                          <tr
                            key={r.id}
                            style={{ borderLeft: '3px solid #0d6efd', cursor: 'pointer' }}
                            onClick={(e) => {
                              if (e.target.closest('button')) return;
                              navigate(`/admin/users/${targetUid}/colaborador/${slug}?weekStart=${r.weekStart}`);
                            }}
                          >
                            <td className="ps-3">
                              <div className="fw-semibold">{weekLabel}</div>
                            </td>
                            <td className="text-center">
                              <span className="badge rounded-pill" style={{ backgroundColor: '#e7f1ff', color: '#0d6efd', fontSize: '0.85em' }}>
                                {totalWorked}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className="badge rounded-pill" style={{ backgroundColor: '#e0f7fa', color: '#0891b2', fontSize: '0.85em' }}>
                                {totalLunch}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className="badge rounded-pill" style={{ backgroundColor: '#fff3e0', color: '#e65100', fontSize: '0.85em' }}>
                                {totalTravel}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className="badge rounded-pill bg-success" style={{ fontSize: '0.9em', padding: '6px 12px' }}>
                                {fechamentoTotal}
                              </span>
                            </td>
                            {salaryConfig && (
                              <td className="text-center">
                                <span className="fw-bold text-success">${salaryText}</span>
                                {salaryGross && (
                                  <div><small className="text-muted">Bruto: ${salaryGross}</small></div>
                                )}
                              </td>
                            )}
                            <td>
                              <small className="text-muted">
                                {lastHist ? format(lastHist.when.toDate(), "dd/MM HH:mm") : "-"}
                              </small>
                            </td>
                            <td className="text-center">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="me-1 px-2 py-1"
                                onClick={() => navigate(`/admin/users/${targetUid}/colaborador/${slug}?weekStart=${r.weekStart}`)}
                                title="Abrir / Editar"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-warning"
                                className="px-2 py-1"
                                onClick={() => recalculateTravel(r)}
                                disabled={recalculating[r.id] || !isLoaded}
                                title="Recalcular Trajetos"
                              >
                                {recalculating[r.id] ? (
                                  <Spinner as="span" animation="border" size="sm" />
                                ) : (
                                  <RefreshCw size={14} />
                                )}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <Card.Footer className="bg-light text-muted small py-2 px-3">
                  {filters.filteredAndSorted.length} relatório{filters.filteredAndSorted.length !== 1 ? 's' : ''}
                </Card.Footer>
              </Card>

              {/* Mobile Cards */}
              <div className="d-md-none">
                {filters.filteredAndSorted.map(r => {
                  const m = computeReportMetrics(r);
                  const weekLabel = format(parseISO(r.weekStart), "dd MMM yyyy");
                  const lastHist = r.history?.slice(-1)[0];
                  const rSlug = encodeURIComponent(userData?.displayName || userData?.email);

                  let salaryText = null;
                  let salaryGross = null;
                  if (salaryConfig) {
                    const totalHrs = m.closingMin / 60;
                    const gross = totalHrs * salaryConfig.rate;
                    if (salaryConfig.deduction > 0) {
                      salaryText = (gross - gross * salaryConfig.deduction).toFixed(2);
                      salaryGross = gross.toFixed(2);
                    } else {
                      salaryText = gross.toFixed(2);
                    }
                  }

                  return (
                    <ReportMobileCard
                      key={r.id}
                      weekLabel={weekLabel}
                      metrics={m}
                      onClick={() => navigate(`/admin/users/${targetUid}/colaborador/${rSlug}?weekStart=${r.weekStart}`)}
                      lastEdit={lastHist ? format(lastHist.when.toDate(), "dd/MM HH:mm") : ""}
                      extraBadges={salaryConfig && (
                        <span className="badge rounded-pill" style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '0.8em' }}>
                          ${salaryText}{salaryGross ? ` (Bruto: $${salaryGross})` : ''}
                        </span>
                      )}
                    >
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="px-2 py-1"
                        onClick={() => navigate(`/admin/users/${targetUid}/colaborador/${rSlug}?weekStart=${r.weekStart}`)}
                        title="Abrir / Editar"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-warning"
                        className="px-2 py-1"
                        onClick={() => recalculateTravel(r)}
                        disabled={recalculating[r.id] || !isLoaded}
                        title="Recalcular Trajetos"
                      >
                        {recalculating[r.id] ? (
                          <Spinner as="span" animation="border" size="sm" />
                        ) : (
                          <RefreshCw size={14} />
                        )}
                      </Button>
                    </ReportMobileCard>
                  );
                })}
                <div className="text-muted small text-center py-2">
                  {filters.filteredAndSorted.length} relatório{filters.filteredAndSorted.length !== 1 ? 's' : ''}
                </div>
              </div>
              </>)}
            </>
          )}
        </Tab>

        {/* Document Signatures Tab */}
        <Tab eventKey="signatures" title={`Documentos Assinados (${signatures.length})`}>
          {signatures.length === 0 ? (
            <Alert variant="info">
              <FileText size={20} className="me-2" />
              Nenhum documento assinado ainda.
            </Alert>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Versão</th>
                  <th>Data de Assinatura</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {signatures.map(sig => (
                  <tr key={sig.id}>
                    <td>{sig.documentTitle}</td>
                    <td>
                      <Badge bg="secondary">v{sig.documentVersion}</Badge>
                    </td>
                    <td>
                      {sig.signedAt?.toDate?.().toLocaleString("pt-BR") || "N/A"}
                    </td>
                    <td>
                      <Badge bg="success">
                        <CheckCircle size={14} className="me-1" />
                        Assinado
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={async () => {
                          try {
                            if (sig.signedDocumentUrl) {
                              // URL direta do Storage
                              window.open(sig.signedDocumentUrl, '_blank');
                            } else if (sig.signedDocumentBase64) {
                              // Base64 direto
                              const link = document.createElement('a');
                              link.href = sig.signedDocumentBase64;
                              link.download = sig.originalFileName || 'documento_assinado.pdf';
                              link.click();
                            } else if (sig.signatureId) {
                              // Reconstruir a partir dos chunks
                              console.log("Baixando chunks...");
                              const chunksQuery = query(
                                collection(db, "documentChunks"),
                                where("signatureId", "==", sig.signatureId)
                              );
                              const chunksSnap = await getDocs(chunksQuery);

                              if (chunksSnap.empty) {
                                alert("Documento não encontrado.");
                                return;
                              }

                              // Ordenar chunks por índice
                              const chunks = chunksSnap.docs
                                .map(doc => doc.data())
                                .sort((a, b) => a.chunkIndex - b.chunkIndex);

                              // Reconstruir base64
                              const fullBase64 = chunks.map(c => c.chunkData).join('');
                              console.log("Arquivo reconstruído!");

                              // Fazer download
                              const link = document.createElement('a');
                              link.href = fullBase64;
                              link.download = sig.originalFileName || 'documento_assinado.pdf';
                              link.click();
                            } else {
                              alert("Documento assinado não disponível para download.");
                            }
                          } catch (error) {
                            console.error("Erro ao baixar documento:", error);
                            alert("Erro ao baixar documento.");
                          }
                        }}
                      >
                        <Download size={14} className="me-1" />
                        Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        {/* Quiz Results Tab */}
        <Tab eventKey="quizzes" title={`Resultados de Treinamento (${quizResults.length})`}>
          {quizResults.length === 0 ? (
            <Alert variant="info">
              <Award size={20} className="me-2" />
              Nenhuma tentativa de treinamento ainda.
            </Alert>
          ) : (
            <div>
              {quizResults.map((result, index) => (
                <Card key={result.id} className="mb-3 shadow-sm">
                  <Card.Body>
                    <Row className="align-items-center">
                      <Col md={8}>
                        <div className="d-flex align-items-center mb-2">
                          <Award size={24} className="text-warning me-2" />
                          <div>
                            <h6 className="mb-0">
                              Tentativa #{result.attemptNumber} - {result.quizTitle}
                            </h6>
                            <small className="text-muted">
                              {result.completedAt?.toDate?.().toLocaleString("pt-BR") || "N/A"}
                            </small>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span>
                              <strong>Pontuação:</strong> {result.score}/{result.totalQuestions}
                            </span>
                            <strong className={result.passed ? "text-success" : "text-danger"}>
                              {result.percentage}%
                            </strong>
                          </div>
                          <ProgressBar
                            now={result.percentage}
                            variant={result.passed ? "success" : "danger"}
                          />
                        </div>

                        {result.categoryScores && (
                          <div>
                            <small className="text-muted">Desempenho por Categoria:</small>
                            <Row className="mt-2">
                              {Object.keys(result.categoryScores).map(category => {
                                const score = result.categoryScores[category];
                                return (
                                  <Col key={category} xs={6} md={4} className="mb-2">
                                    <small>
                                      <strong>{category}:</strong> {score.correct}/{score.total}
                                    </small>
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        )}
                      </Col>

                      <Col md={4} className="text-center">
                        <Badge
                          bg={result.passed ? "success" : "danger"}
                          className="fs-6 py-2 px-3"
                        >
                          {result.passed ? "✅ APROVADO" : "❌ REPROVADO"}
                        </Badge>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Tab>
      </Tabs>
    </Container>
  );
}
